import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Table,
    TableBody,
    TableRow,
} from '@mui/material';
import { Fragment, memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type {
    ClinicalPeopleList,
    PatientSummaryDto,
} from '~/backend/connector-next-api/connector-next-schemas';
import {
    patientCourseStageMeta,
    resolveCourseStage,
    type ClinicalLists,
    type CourseStage,
    type Sort,
    type SortValue,
} from '../model';
import CourseStageDot from '../shared/CourseStageDot';
import LoadMoreFooter from './LoadMoreFooter';
import PatientListHeader from './PatientListHeader';
import { useIsOpeningInViewer, useOpenInViewer } from '~/hooks/openInViewer';
import PatientRow from './PatientRow';
import {
    LIST_MIN_WIDTH_COMPACT,
    LIST_MIN_WIDTH_FULL,
    PatientListColgroup,
    PatientListGroupCell,
    visiblePatientListColumns,
} from './PatientListTable';

type PatientListProps = {
    patients: PatientSummaryDto[];
    /** Patients matching the query server-side; may exceed those loaded. */
    totalCount: number;
    hasMore: boolean;
    onLoadMore: () => void;
    isLoadingMore: boolean;
    isLoading: boolean;
    errorMessage: string | undefined;
    compact: boolean;
    selectedId: string | undefined;
    onSelect: (id: string) => void;
    sort: Sort;
    onSortChange: (key: SortValue) => void;
    /** Whether the search term or a filter narrowed the query behind these results. */
    isNarrowed: boolean;
    onClearSearchAndFilters: () => void;
    lists: ClinicalLists;
    people: ClinicalPeopleList | undefined;
    /** Whether the user may assign patients to anybody, from each row. */
    canAssign: boolean;
    /** Today, injected so the rows' ages and treatment start warnings are testable. */
    today: Date;
};

/** The patient list: loading/error/empty states or the header+rows grid. */
const PatientList = memo(function PatientList({
    patients,
    totalCount,
    hasMore,
    onLoadMore,
    isLoadingMore,
    isLoading,
    errorMessage,
    compact,
    selectedId,
    onSelect,
    sort,
    onSortChange,
    isNarrowed,
    onClearSearchAndFilters,
    lists,
    people,
    canAssign,
    today,
}: PatientListProps) {
    const intl = useIntl();
    // Owned by the list, not the row: the list renders every loaded patient
    // without virtualization, so one subscription serves all of them.
    const openInViewer = useOpenInViewer();
    const openBusy = useIsOpeningInViewer();

    // Headers only when the list is ordered by stage. The server returns the
    // rows in that order, so equal stages are contiguous and a header is due
    // wherever the stage changes; under any other sort the stages interleave
    // and a "group" would be one row long.
    const grouped = sort.key === 'courseStage';

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
            </Box>
        );
    }
    if (errorMessage !== undefined) {
        return (
            <Alert severity="error" sx={{ m: 3 }}>
                {errorMessage}
            </Alert>
        );
    }
    if (patients.length === 0) {
        // A narrowed empty result and an empty cohort are different facts; the
        // unnarrowed copy's "go ingest data" is the wrong instruction here.
        return isNarrowed ? (
            <Alert
                severity="info"
                sx={{ m: 3 }}
                action={
                    <Button size="small" onClick={onClearSearchAndFilters}>
                        <FormattedMessage id="patientList.filter.clearAll" />
                    </Button>
                }
            >
                <FormattedMessage id="patientList.emptyFiltered" />
            </Alert>
        ) : (
            <Alert severity="info" sx={{ m: 3 }}>
                <FormattedMessage id="patientList.empty" />
            </Alert>
        );
    }
    return (
        <>
            <Table
                stickyHeader
                sx={{
                    tableLayout: 'fixed',
                    minWidth: compact
                        ? LIST_MIN_WIDTH_COMPACT
                        : LIST_MIN_WIDTH_FULL,
                }}
            >
                <PatientListColgroup compact={compact} />
                <PatientListHeader
                    compact={compact}
                    sort={sort}
                    onSortChange={onSortChange}
                />
                <TableBody>
                    {patients.map((patient, index) => {
                        const stage = resolveCourseStage(patient.courseStage);
                        const previous =
                            index === 0
                                ? undefined
                                : resolveCourseStage(
                                      patients[index - 1].courseStage
                                  );
                        const key = patient.id ?? patient.dicomPatientId;
                        return (
                            <Fragment key={key}>
                                {grouped && stage !== previous && (
                                    <StageGroupHeader
                                        stage={stage}
                                        count={countFrom(
                                            patients,
                                            index,
                                            stage
                                        )}
                                        columnCount={
                                            visiblePatientListColumns(compact)
                                                .length
                                        }
                                        label={
                                            patientCourseStageMeta(intl, stage)
                                                .label
                                        }
                                    />
                                )}
                                <PatientRow
                                    patient={patient}
                                    compact={compact}
                                    selected={
                                        patient.id !== undefined &&
                                        patient.id === selectedId
                                    }
                                    onSelect={onSelect}
                                    onOpenInViewer={openInViewer}
                                    openBusy={openBusy}
                                    lists={lists}
                                    people={people}
                                    canAssign={canAssign}
                                    today={today}
                                />
                            </Fragment>
                        );
                    })}
                </TableBody>
            </Table>
            <LoadMoreFooter
                loadedCount={patients.length}
                totalCount={totalCount}
                hasMore={hasMore}
                onLoadMore={onLoadMore}
                isLoadingMore={isLoadingMore}
                isNarrowed={isNarrowed}
            />
        </>
    );
});

/**
 * Rows sharing `stage` from `index` onwards. The count is of what is loaded,
 * not of the cohort: the list walks pages, so a group can grow as more
 * arrive, and claiming a server-side total here would be a number the visible
 * rows contradict.
 */
function countFrom(
    patients: readonly PatientSummaryDto[],
    index: number,
    stage: CourseStage
): number {
    let count = 0;
    for (let i = index; i < patients.length; i++) {
        if (resolveCourseStage(patients[i].courseStage) !== stage) {
            break;
        }
        count++;
    }
    return count;
}

function StageGroupHeader({
    stage,
    label,
    count,
    columnCount,
}: {
    stage: CourseStage;
    label: string;
    count: number;
    columnCount: number;
}) {
    return (
        <TableRow data-stage-group={stage}>
            <PatientListGroupCell colSpan={columnCount}>
                <Box
                    component="span"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <CourseStageDot stage={stage} />
                    <FormattedMessage
                        id="patientList.stage.group"
                        values={{ stage: label, count }}
                    />
                </Box>
            </PatientListGroupCell>
        </TableRow>
    );
}

export default PatientList;
